import React, { useMemo } from "react"
import { Table, Space, Typography, message } from "@mimber/ui-web"
import { HomeOutlined, ClockCircleOutlined } from "@mimber/ui-web"
import { useHouseholds } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { HouseholdDto } from "@mimber/schema"
import type { HouseholdsTableProps } from "./HouseholdsTable.types"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const HouseholdsTable: React.FunctionComponent<HouseholdsTableProps> = ({
  onRowClick,
  className,
  ...tableProps
}) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "households",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
    })

  const { data: response, isLoading, error } = useHouseholds(queryParams)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load households: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<HouseholdDto> = useMemo(
    () => [
      {
        title: "Household",
        key: "name",
        dataIndex: "name",
        render: (name: string | null, record) => (
          <Space>
            <HomeOutlined />
            <Text strong>{name || `Household ${record.uuid.slice(0, 8)}`}</Text>
          </Space>
        ),
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search household name"),
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (createdAt: string) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(createdAt)}</Text>
          </Space>
        ),
      },
    ],
    []
  )

  const handleRowClick = (record: HouseholdDto) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const onTableChange: TableProps<HouseholdDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <Table<HouseholdDto>
      bordered
      columns={displayColumns}
      dataSource={response?.data || []}
      loading={isLoading}
      rowKey="uuid"
      pagination={paginationConfig}
      locale={getRememberedTableLocale(resetQueryParams, queryParams)}
      onChange={onTableChange}
      onRow={(record) => ({
        onClick: () => handleRowClick(record),
        style: { cursor: onRowClick ? "pointer" : "default" },
      })}
      className={className}
      {...tableProps}
    />
  )
}
